import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/api/client";

interface TwoFAStatus {
  enabled: boolean;
}

interface TwoFABeginResponse {
  secret: string;
  otpauth_uri: string;
  issuer: string;
  account: string;
}

export function SettingsPage() {
  return (
    <div>
      <header className="mb-6">
        <h1 className="text-xl font-semibold">Settings</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
          Account security and personal preferences.
        </p>
      </header>
      <section className="mb-8">
        <h2 className="text-base font-semibold mb-3">Two-factor authentication</h2>
        <TwoFASection />
      </section>
      <section className="mb-8">
        <h2 className="text-base font-semibold mb-3">Push notifications</h2>
        <PushSection />
      </section>
    </div>
  );
}

function TwoFASection() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState<TwoFABeginResponse | null>(null);
  const [code, setCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [showDisable, setShowDisable] = useState(false);

  const status = useQuery<TwoFAStatus>({
    queryKey: ["me-2fa"],
    queryFn: () => api<TwoFAStatus>("/api/v1/me/2fa"),
    staleTime: 30_000,
  });

  const onErr = (e: unknown) => {
    if (e instanceof ApiError) {
      const body = (typeof e.body === "object" && e.body) ? (e.body as { error?: string }) : null;
      setError(body?.error ?? e.message);
    } else {
      setError(String(e));
    }
  };

  const begin = useMutation({
    mutationFn: () => api<TwoFABeginResponse>("/api/v1/me/2fa/begin", { method: "POST" }),
    onSuccess: (r) => { setEnrolling(r); setCode(""); setError(null); setNotice(null); },
    onError: onErr,
  });

  const verify = useMutation({
    mutationFn: (input: { secret: string; code: string }) =>
      api("/api/v1/me/2fa/verify", { method: "POST", body: input }),
    onSuccess: () => {
      setEnrolling(null);
      setCode("");
      setNotice("Two-factor authentication is now enabled. You'll be prompted for a code on your next sign-in.");
      setError(null);
      qc.invalidateQueries({ queryKey: ["me-2fa"] });
    },
    onError: onErr,
  });

  const disable = useMutation({
    mutationFn: (input: { password: string }) =>
      api("/api/v1/me/2fa", { method: "DELETE", body: input }),
    onSuccess: () => {
      setShowDisable(false);
      setDisablePassword("");
      setNotice("Two-factor authentication is disabled.");
      setError(null);
      qc.invalidateQueries({ queryKey: ["me-2fa"] });
    },
    onError: onErr,
  });

  if (status.isLoading) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm">
      {error && (
        <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3 flex items-start justify-between gap-3">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-xs text-red-600 hover:text-red-900">dismiss</button>
        </div>
      )}
      {notice && (
        <div className="rounded bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700 mb-3 flex items-start justify-between gap-3">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-xs text-green-700 hover:text-green-900">dismiss</button>
        </div>
      )}

      {status.data?.enabled && !showDisable && (
        <div>
          <p className="text-slate-700 dark:text-slate-200 mb-2">
            <span className="inline-block rounded bg-green-100 text-green-800 px-2 py-0.5 text-xs font-semibold uppercase mr-2">Enabled</span>
            You'll be prompted for a 6-digit code from your authenticator app on every sign-in.
          </p>
          <button
            onClick={() => setShowDisable(true)}
            className="mt-2 rounded border border-red-300 text-red-700 px-3 py-1 text-xs hover:bg-red-50"
          >
            Disable two-factor authentication
          </button>
        </div>
      )}

      {status.data?.enabled && showDisable && (
        <form
          onSubmit={(e) => { e.preventDefault(); disable.mutate({ password: disablePassword }); }}
          className="space-y-2"
        >
          <p className="text-slate-700 dark:text-slate-200">
            Confirm your password to disable two-factor authentication.
          </p>
          <input
            type="password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
            required
            autoComplete="current-password"
            placeholder="Current password"
            className="w-full rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-700 px-2 py-1 font-mono text-sm"
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={disable.isPending || !disablePassword}
              className="rounded bg-red-600 text-white px-3 py-1 text-xs hover:bg-red-700 disabled:opacity-30"
            >{disable.isPending ? "Disabling…" : "Confirm disable"}</button>
            <button
              type="button"
              onClick={() => { setShowDisable(false); setDisablePassword(""); }}
              className="rounded border border-slate-300 px-3 py-1 text-xs hover:bg-slate-100 dark:hover:bg-slate-700"
            >Cancel</button>
          </div>
        </form>
      )}

      {!status.data?.enabled && !enrolling && (
        <div>
          <p className="text-slate-700 dark:text-slate-200 mb-3">
            <span className="inline-block rounded bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200 px-2 py-0.5 text-xs font-semibold uppercase mr-2">Disabled</span>
            Add a second factor to require an authenticator code in addition
            to your password.
          </p>
          <button
            onClick={() => begin.mutate()}
            disabled={begin.isPending}
            className="rounded bg-slate-900 dark:bg-purple-600 text-white px-3 py-1 text-xs hover:bg-slate-800 dark:hover:bg-purple-500 disabled:opacity-50"
          >
            {begin.isPending ? "Generating…" : "Enable two-factor authentication"}
          </button>
        </div>
      )}

      {!status.data?.enabled && enrolling && (
        <form
          onSubmit={(e) => { e.preventDefault(); verify.mutate({ secret: enrolling.secret, code }); }}
          className="space-y-3"
        >
          <p className="text-slate-700 dark:text-slate-200">
            Add this account to your authenticator app (Google Authenticator,
            1Password, Authy, Bitwarden), then enter the current 6-digit code
            to confirm.
          </p>
          <div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Account secret (manual entry)</div>
            <CopyField value={enrolling.secret} />
          </div>
          <div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">
              Or paste this URI into your app
            </div>
            <CopyField value={enrolling.otpauth_uri} small />
          </div>
          <label className="block">
            <span className="text-xs text-slate-500 dark:text-slate-400">6-digit code</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              autoFocus
              required
              className="mt-1 block w-full rounded border-slate-300 dark:border-slate-600 dark:bg-slate-700 border px-3 py-2 font-mono text-lg text-center tracking-widest"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={verify.isPending || code.length !== 6}
              className="rounded bg-slate-900 dark:bg-purple-600 text-white px-3 py-1 text-xs hover:bg-slate-800 dark:hover:bg-purple-500 disabled:opacity-50"
            >{verify.isPending ? "Verifying…" : "Verify and enable"}</button>
            <button
              type="button"
              onClick={() => { setEnrolling(null); setCode(""); }}
              className="rounded border border-slate-300 px-3 py-1 text-xs hover:bg-slate-100 dark:hover:bg-slate-700"
            >Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

// --- Push notifications -----------------------------------------------------

function urlBase64ToUint8Array(b64: string): Uint8Array {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const s = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(s);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function PushSection() {
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<"" | "enable" | "disable" | "test">("");
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "default",
  );

  const supported = pushSupported();

  // Probe current subscription state on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!supported) {
        if (!cancelled) setSubscribed(false);
        return;
      }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setSubscribed(!!sub);
      } catch {
        if (!cancelled) setSubscribed(false);
      }
    })();
    return () => { cancelled = true; };
  }, [supported]);

  if (!supported) {
    return (
      <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm">
        <p className="text-slate-700 dark:text-slate-200">
          Your browser does not support web push notifications. Try a recent
          Chrome, Edge, Firefox, or Safari (macOS 13+/iOS 16.4+).
        </p>
      </div>
    );
  }

  const enable = async () => {
    setBusy("enable"); setError(null); setNotice(null);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        throw new Error("Notification permission denied — enable it in your browser settings, then try again.");
      }
      const reg = await navigator.serviceWorker.ready;
      // Fetch the VAPID public key.
      const r = await fetch("/api/v1/push/vapid-key", { credentials: "same-origin" });
      if (!r.ok) throw new Error("could not fetch VAPID key (" + r.status + ")");
      const { public_key } = await r.json();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key) as unknown as BufferSource,
      });
      const json = sub.toJSON() as {
        endpoint: string;
        keys?: { p256dh?: string; auth?: string };
      };
      await api("/api/v1/push/subscribe", {
        method: "POST",
        body: {
          endpoint: json.endpoint,
          keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
        },
      });
      setSubscribed(true);
      setNotice("Push notifications enabled for this device + browser.");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy("");
    }
  };

  const disable = async () => {
    setBusy("disable"); setError(null); setNotice(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try {
          await api("/api/v1/push/subscribe", {
            method: "DELETE",
            body: { endpoint: sub.endpoint },
          });
        } catch {/* still try to unsub locally */}
        await sub.unsubscribe();
      }
      setSubscribed(false);
      setNotice("Push notifications disabled for this device.");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy("");
    }
  };

  const test = async () => {
    setBusy("test"); setError(null); setNotice(null);
    try {
      const r = await api<{ sent: number; failed: number }>("/api/v1/push/test", { method: "POST" });
      setNotice(`Test sent — ${r.sent} delivered${r.failed ? `, ${r.failed} failed` : ""}.`);
    } catch (e) {
      if (e instanceof ApiError) {
        const body = (typeof e.body === "object" && e.body) ? (e.body as { error?: string }) : null;
        setError(body?.error ?? e.message);
      } else {
        setError(String(e));
      }
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm space-y-3">
      {error && (
        <div className="rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2 text-red-700 dark:text-red-300 flex items-start justify-between gap-3">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-xs text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-200">dismiss</button>
        </div>
      )}
      {notice && (
        <div className="rounded bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 px-3 py-2 text-green-700 dark:text-green-300 flex items-start justify-between gap-3">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-xs text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-200">dismiss</button>
        </div>
      )}

      <p className="text-slate-700 dark:text-slate-200">
        Operator and admin events (e.g. a new device being enrolled in the
        fleet) can be delivered as native browser notifications. Permission
        is per-device + per-browser; you'll need to enable each one.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {subscribed ? (
          <>
            <span className="inline-block rounded bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 px-2 py-0.5 text-xs font-semibold uppercase">Enabled</span>
            <button
              onClick={test}
              disabled={busy !== ""}
              className="rounded bg-slate-900 dark:bg-purple-600 text-white px-3 py-1 text-xs hover:bg-slate-800 dark:hover:bg-purple-500 disabled:opacity-50"
            >{busy === "test" ? "Sending…" : "Send test notification"}</button>
            <button
              onClick={disable}
              disabled={busy !== ""}
              className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1 text-xs hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
            >{busy === "disable" ? "Disabling…" : "Disable on this device"}</button>
          </>
        ) : (
          <>
            <span className="inline-block rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 px-2 py-0.5 text-xs font-semibold uppercase">Disabled</span>
            <button
              onClick={enable}
              disabled={busy !== "" || permission === "denied"}
              className="rounded bg-slate-900 dark:bg-purple-600 text-white px-3 py-1 text-xs hover:bg-slate-800 dark:hover:bg-purple-500 disabled:opacity-50"
            >{busy === "enable" ? "Requesting…" : "Enable on this device"}</button>
            {permission === "denied" && (
              <span className="text-xs text-amber-700 dark:text-amber-300">
                Notifications are blocked in your browser — unblock via the address-bar lock icon, then reload.
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CopyField({ value, small }: { value: string; small?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {/* clipboard blocked */}
  };
  return (
    <div className="flex gap-2">
      <input
        type="text"
        readOnly
        value={value}
        onFocus={(e) => e.currentTarget.select()}
        className={`flex-1 font-mono ${small ? "text-xs" : "text-sm"} bg-slate-50 dark:bg-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded px-2 py-1`}
      />
      <button
        type="button"
        onClick={copy}
        className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs hover:bg-slate-100 dark:hover:bg-slate-700"
      >{copied ? "Copied!" : "Copy"}</button>
    </div>
  );
}
